// @ts-strict-ignore
import { DateTime, IANAZone } from 'luxon';
import { toDateTime } from './chronon';
import { DateLike } from './nova-date';

function isValidTimezone(timezone: string): boolean {
  return typeof timezone === 'string' && IANAZone.isValidZone(timezone);
}

function parseDateInZone(input: string, zone: string): DateTime {
  const fromISO = DateTime.fromISO(input, { zone });
  if (fromISO.isValid) {
    return fromISO;
  }
  return DateTime.fromSQL(input, { zone });
}

function getOrdinalSuffix(day: number): string {
  if (day >= 11 && day <= 13) {
    return 'th';
  }
  switch (day % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
}

/**
 * @deprecated Use chronon's formatDateTimeWithMilitarySupport or toDateTime().toFormat()
 *
 * Renders a UTC date in the given timezone with an optional format string.
 * When formatString is null, returns ISO string.
 * When formatString is omitted (default), returns human-readable format
 * matching the legacy output: "Mon, Jan 1st 2024 @ 5:30PM - EST"
 */
export function renderUtcInTimezone(
  utcDate: Date,
  timezone: string,
  formatString?: string | null
): string {
  if (!isValidTimezone(timezone)) {
    throw new Error(`Core: renderUtcInTimezone(): Invalid timezone ${timezone}`);
  }

  let dt: DateTime;
  try {
    dt = toDateTime(utcDate as DateLike, { zone: timezone });
  } catch {
    return 'Invalid date';
  }

  if (!dt.isValid) {
    return 'Invalid date';
  }

  if (formatString === null) {
    return dt.toISO({ suppressMilliseconds: true });
  }

  if (typeof formatString === 'undefined') {
    const day = dt.day;
    const ordinal = `${day}${getOrdinalSuffix(day)}`;
    const weekday = dt.toFormat('EEE');
    const month = dt.toFormat('MMM');
    const year = dt.toFormat('yyyy');
    const time = dt.toFormat('h:mma').toUpperCase();
    const tz = dt.toFormat('ZZZZ');
    return `${weekday}, ${month} ${ordinal} ${year} @ ${time} - ${tz}`;
  }

  return dt.toFormat(formatString);
}

export function transformForUserFriendlyTimezone(
  formattedDateTime: string,
  timezone: string
): string {
  // eslint-disable-next-line prefer-named-capture-group
  return formattedDateTime.replace(/(GMT)?[+-]\d{1,4}/u, timezone);
}

/**
 * @deprecated Use toDateTime(timestamp, { zone: timezone }).toUTC().toISO()
 *
 * Parses a timestamp in the given timezone and returns it as UTC.
 * When formatString is null (default), returns ISO string.
 */
export function renderTimezoneInUtc(
  timestamp: string,
  timezone: string,
  formatString: string | null = null
): string {
  if (!isValidTimezone(timezone)) {
    throw new Error(`Core: renderTimezoneInUtc(): Invalid timezone ${timezone}`);
  }

  const dt = parseDateInZone(timestamp, timezone).toUTC();

  if (!dt.isValid) {
    return 'Invalid date';
  }

  if (formatString === null) {
    return dt.toISO({ suppressMilliseconds: true });
  }

  return dt.toFormat(formatString);
}

export function getFullDayIntervalFromDateInTimezone(
  date: string,
  timezone: string
): Record<string, unknown> {
  if (!isValidTimezone(timezone)) {
    throw new Error(`Core: getFullDayIntervalFromDateInTimezone(): Invalid timezone ${timezone}`);
  }
  const dt = parseDateInZone(date, timezone);
  return {
    start: dt.startOf('day').set({ millisecond: 0 }).toISO({ suppressMilliseconds: true }),
    end: dt.endOf('day').set({ millisecond: 0 }).toISO({ suppressMilliseconds: true })
  };
}

export function utcNow(): string {
  return DateTime.utc().toFormat("yyyy-MM-dd'T'HH:mm:ss'Z'");
}

export function utcNowAsDate(): Date {
  return DateTime.utc().toJSDate();
}

export function isPastDate(dateString: string): boolean {
  const checkDate = new Date(dateString);
  checkDate.setUTCHours(23, 59, 59, 599);
  const currentDate = new Date();
  currentDate.setUTCHours(23, 59, 59, 599);
  return checkDate < currentDate;
}

export function isFutureDate(dateString: string): boolean {
  return DateTime.fromISO(dateString).diffNow('seconds').seconds > 0;
}

export type InputDate =
  | `${number}${number}${number}${number}-${number}${number}-${number}${number}`
  | Date;

type DiffBetweenDatesParams = {
  startDateStr: InputDate;
  endDateStr: InputDate;
  diffUnit?: string;
};

export function diffBetweenDates({
  startDateStr,
  endDateStr,
  diffUnit = 'weeks'
}: DiffBetweenDatesParams): number {
  const luxonUnit = diffUnit.endsWith('s') ? diffUnit : `${diffUnit}s`;
  const startDate = toDateTime(startDateStr as DateLike);
  const endDate = toDateTime(endDateStr as DateLike);

  // Whole calendar days between local midnights — matches date-range validators that
  // normalize start to 00:00:00 and end to 23:59:59.999 (would otherwise be ~5.999 "days").
  if (luxonUnit === 'days') {
    return Math.abs(endDate.startOf('day').diff(startDate.startOf('day'), 'days').days);
  }

  return Math.trunc(Math.abs(endDate.diff(startDate, luxonUnit as any)[luxonUnit]));
}

export function getNowInTimezone(timezone?: string): DateTime {
  let now: DateTime = DateTime.now();
  if (timezone) {
    now = now.setZone(timezone);
  }
  return now;
}
